import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { mediaForPath } from "../data/businessMedia";

export default function BusinessMediaGallery() {
  const { pathname } = useLocation();
  const media = useMemo(() => mediaForPath(pathname), [pathname]);
  const tagalog = window.localStorage.getItem("mysitecare-language") === "tl";
  const sectionTitle = (media?.category.includes("project") || media?.category.includes("company portfolio"))
    ? (tagalog ? "Higit Pang Gawa sa Proyekto" : "More Project Work")
    : media?.category.includes("jewelry")
      ? (tagalog ? "Higit Pang Alahas" : "More Jewelry")
      : (tagalog ? "Higit Pang Larawan ng Klinika at Paggamot" : "More Clinic & Treatment Images");
  const [mount, setMount] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const closeButton = useRef(null);

  useEffect(() => {
    if (!media) {
      setMount(null);
      return undefined;
    }

    const footer = document.querySelector("footer");
    if (!footer?.parentNode) return undefined;
    const contactSection = document.querySelector("section#contact, section#visit, #contact, #visit");

    const node = document.createElement("div");
    node.dataset.mscBusinessMedia = pathname;
    if (contactSection?.parentNode) contactSection.parentNode.insertBefore(node, contactSection);
    else footer.parentNode.insertBefore(node, footer);
    setMount(node);

    return () => {
      setActiveImage(null);
      node.remove();
      setMount(null);
    };
  }, [pathname, media]);

  useEffect(() => {
    if (!activeImage) return undefined;
    const close = event => event.key === "Escape" && setActiveImage(null);
    document.addEventListener("keydown", close);
    document.body.classList.add("msc-media-open");
    closeButton.current?.focus();
    return () => {
      document.removeEventListener("keydown", close);
      document.body.classList.remove("msc-media-open");
    };
  }, [activeImage]);

  if (!media || !mount) return null;

  return createPortal(
    <>
      <section className="msc-media" aria-labelledby="msc-media-title">
        <div className="msc-media__inner">
          <p className="msc-media__eyebrow">{tagalog ? "Higit pang matutuklasan" : "More to explore"}</p>
          <h2 id="msc-media-title">{sectionTitle}</h2>
          <p className="msc-media__business">{media.name}</p>
          <p className="msc-media__intro">
            {tagalog
              ? "Mga karagdagang larawang ibinigay para sa konseptong ito, pinili upang umakma sa pangunahing pahina nang hindi inuulit ang kasalukuyang gallery."
              : "Additional images supplied for this concept, selected to complement the main page without repeating its existing gallery."}
          </p>
          <div className={`msc-media__grid msc-media__grid--${Math.min(media.images.length, 4)}`}>
            {media.images.map((image, index) => (
              <button
                type="button"
                className="msc-media__item"
                key={image.src}
                onClick={() => setActiveImage(image)}
                aria-label={`Open image ${index + 1} of ${media.images.length}`}
              >
                <picture>
                  <source srcSet={`${image.thumbnail} 800w, ${image.webp} 1600w`} sizes="(max-width: 760px) 50vw, 34vw" type="image/webp" />
                  <img src={image.src} alt={image.alt} loading="lazy" decoding="async" />
                </picture>
              </button>
            ))}
          </div>
          <p className="msc-media__source">{tagalog ? "Mga larawan ng portfolio at pampublikong negosyo mula sa ibinigay na research materials." : "Portfolio and public-business imagery supplied in the research materials."}</p>
        </div>
      </section>

      {activeImage && (
        <div className="msc-lightbox" role="dialog" aria-modal="true" aria-label="Image preview" onClick={() => setActiveImage(null)}>
          <button ref={closeButton} type="button" className="msc-lightbox__close" onClick={() => setActiveImage(null)} aria-label="Close image preview">×</button>
          <picture onClick={event => event.stopPropagation()}>
            <source srcSet={activeImage.webp} type="image/webp" />
            <img src={activeImage.src} alt={activeImage.alt} />
          </picture>
        </div>
      )}
    </>,
    mount
  );
}
